import { useCallback, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { isAxiosError } from 'axios'
import {
    generateImagesApiV1GenerationsOrgIdGenerateImagesProductProductIdPost,
    generateTitleDescriptionApiV1GenerationsOrgIdGenerateTitleProductProductIdPost,
    getCardValidationDetailApiV1GenerationsOrgIdGenerationGenerationIdCardValidationGet,
} from '@/common/api/client/sdk.gen'
import type {
    BodyGenerateImagesApiV1GenerationsOrgIdGenerateImagesProductProductIdPost,
    CardValidationDetailResponse,
    GenerationCreateResponse,
} from '@/common/api/client/types.gen'
import { resolveRelativeAssetUrl } from '@/common/lib/assetsUrl'
import { useOptionalToast } from '@/common/ui/Toast/toastContext'
import { isInsufficientBalanceError, openInsufficientBalanceModal } from '@/app/entities/insufficientBalance/model'
import { addCompetition } from '@/app/entities/competition/api'
import { useCurrentOrg } from '@/app/entities/org/model'
import { fetchGenerateImageCategories } from '../api/productCategoryOptions'
import { fetchGenerateImageVariants } from '../api/productImageVariants'

export const PRODUCT_AUTO_PIPELINE_COMPETITION_URL =
    'https://www.ozon.ru/product/shkaf-dlya-podogreva-posudy-kuppersberg-kwd-600-black-modifikatsiya-2026-goda-1214934453/?at=r2t4zGmXwH5Lv4P8SPQGG3xcD9ww89IA7AGQ6iWD7gRZ'

const CARD_VALIDATION_POLL_INTERVAL_MS = 2000
const CARD_VALIDATION_POLL_TIMEOUT_MS = 10 * 60 * 1000
const CARD_VALIDATION_FAILED_ERROR_MESSAGE = 'Аналитика завершилась с ошибкой'

export type ProductAutoPipelineStep = 'competition' | 'card-validation' | 'title' | 'images' | 'done'

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

const isCardValidationReady = (detail: CardValidationDetailResponse): boolean => {
    const isTerminalStatus = detail.status === 'completed'
    if (isTerminalStatus) {
        return true
    }

    const hasImages = Array.isArray(detail.product_data?.images) && detail.product_data.images.length > 0
    return hasImages
}

const getProductIdFromCompetitionResponse = (response: GenerationCreateResponse): string => {
    const productId = response.additional_info?.product_id
    if (typeof productId !== 'string' || !productId) {
        throw new Error('Не получен ID товара после анализа')
    }
    return productId
}

export const startCompetitionAnalysis = async (orgId: string, url: string): Promise<GenerationCreateResponse> =>
    addCompetition(orgId, { url })

export const pollCardValidationReady = async (
    orgId: string,
    generationId: string
): Promise<CardValidationDetailResponse> => {
    const startedAt = Date.now()

    while (Date.now() - startedAt < CARD_VALIDATION_POLL_TIMEOUT_MS) {
        const res = await getCardValidationDetailApiV1GenerationsOrgIdGenerationGenerationIdCardValidationGet({
            path: { org_id: orgId, generation_id: generationId },
        })
        const detail = res.data!
        if (detail.status === 'failed') {
            throw new Error(CARD_VALIDATION_FAILED_ERROR_MESSAGE)
        }
        if (isCardValidationReady(detail)) {
            return detail
        }
        await sleep(CARD_VALIDATION_POLL_INTERVAL_MS)
    }

    throw new Error('Превышено время ожидания данных аналитики')
}

export const startTitleGeneration = async (orgId: string, productId: string): Promise<void> => {
    await generateTitleDescriptionApiV1GenerationsOrgIdGenerateTitleProductProductIdPost({
        path: { org_id: orgId, product_id: productId },
        body: {},
    })
}

const fetchUrlAsFile = async (url: string, index: number): Promise<File> => {
    const resolvedUrl = resolveRelativeAssetUrl(url) ?? url
    const response = await fetch(resolvedUrl)
    if (!response.ok) {
        throw new Error(`Не удалось загрузить изображение: ${url}`)
    }
    const blob = await response.blob()
    const extension = blob.type.split('/')[1] || 'jpg'
    return new File([blob], `asset-${index}.${extension}`, { type: blob.type || 'image/jpeg' })
}

export const assetUrlsToFiles = async (urls: string[]): Promise<File[]> =>
    Promise.all(urls.map((url, index) => fetchUrlAsFile(url, index)))

const buildGenerateImagesBody = async (
    orgId: string,
    detail: CardValidationDetailResponse
): Promise<BodyGenerateImagesApiV1GenerationsOrgIdGenerateImagesProductProductIdPost> => {
    const categories = await fetchGenerateImageCategories(orgId)
    const categoryId = categories.find(item => item.category_id)?.category_id
    if (!categoryId) {
        throw new Error('Не найдены категории для генерации изображений')
    }

    const variants = await fetchGenerateImageVariants(orgId, categoryId)
    const variantIds = variants
        .map(item => item.id)
        .filter((id): id is string => Boolean(id))
        .slice(0, 3)

    if (!variantIds.length) {
        throw new Error('Не найдены варианты изображений для генерации')
    }

    const productDataImagesUrls = (detail.product_data?.images ?? []).slice(0, 5)
    const images = await assetUrlsToFiles(productDataImagesUrls)

    return {
        category_id: categoryId,
        image_variants: variantIds,
        image_quantites: variantIds.map(() => 1),
        images,
        font: 'Roboto',
        product_name: detail.product_data?.title ?? null,
        product_description: detail.product_data?.description ?? null,
        font_color: null,
        bg_color: null,
        bg_property: null,
        bg_image: null,
        font_image_image: null,
    }
}

export const startImagesGeneration = async (
    orgId: string,
    productId: string,
    detail: CardValidationDetailResponse
): Promise<void> => {
    const body = await buildGenerateImagesBody(orgId, detail)
    await generateImagesApiV1GenerationsOrgIdGenerateImagesProductProductIdPost({
        path: { org_id: orgId, product_id: productId },
        body,
    })
}

export const runProductAutoPipeline = async (
    orgId: string,
    url: string,
    onStepChange: (step: ProductAutoPipelineStep) => void,
    onCompetitionSuccess?: () => void
): Promise<{ productId: string }> => {
    onStepChange('competition')
    const competitionResponse = await startCompetitionAnalysis(orgId, url)
    onCompetitionSuccess?.()
    const generationId = competitionResponse.generation_id
    const productId = getProductIdFromCompetitionResponse(competitionResponse)

    onStepChange('card-validation')
    const cardValidationDetail = await pollCardValidationReady(orgId, generationId)

    onStepChange('title')
    await startTitleGeneration(orgId, productId)

    onStepChange('images')
    await startImagesGeneration(orgId, productId, cardValidationDetail)

    onStepChange('done')
    return { productId }
}

const getPipelineErrorMessage = (error: unknown): string => {
    if (isAxiosError(error)) {
        const detail = error.response?.data?.detail
        if (typeof detail === 'string') {
            return detail
        }
    }
    if (error instanceof Error && error.message) {
        return error.message
    }
    return 'Ошибка при автогенерации'
}

export const closeProductModal = (modalId: string) => {
    const checkbox = document.getElementById(modalId) as HTMLInputElement | null
    if (checkbox) {
        checkbox.checked = false
    }
}

export const useProductAutoPipeline = (modalId: string = 'create-product-modal') => {
    const { data: currentOrg } = useCurrentOrg()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const toastContext = useOptionalToast()
    const [step, setStep] = useState<ProductAutoPipelineStep | null>(null)

    const mutation = useMutation({
        mutationFn: async (competitionUrl: string) => {
            if (!currentOrg?.id) {
                throw new Error('Организация не выбрана')
            }
            return runProductAutoPipeline(currentOrg.id, competitionUrl, setStep, () => {
                void queryClient.invalidateQueries({ queryKey: ['products-infinite'] })
            })
        },
        onSuccess: ({ productId }) => {
            void queryClient.invalidateQueries({ queryKey: ['products-infinite'] })
            closeProductModal(modalId)
            setStep(null)
            toastContext?.showToast({ type: 'success', message: 'Генерация запущена' })
            navigate(`/app/products/${productId}`)
        },
        onError: (error: unknown) => {
            setStep(null)
            if (isInsufficientBalanceError(error)) {
                openInsufficientBalanceModal()
                return
            }
            toastContext?.showToast({ type: 'error', message: getPipelineErrorMessage(error) })
        },
    })

    const handleRunPipeline = useCallback(
        (competitionUrl: string = PRODUCT_AUTO_PIPELINE_COMPETITION_URL) => {
            mutation.mutate(competitionUrl)
        },
        [mutation]
    )

    const stepLabel = (() => {
        switch (step) {
            case 'competition':
                return 'Анализ конкурента…'
            case 'card-validation':
                return 'Ожидание данных аналитики…'
            case 'title':
                return 'Генерация текстов и SEO…'
            case 'images':
                return 'Генерация изображений…'
            case 'done':
                return 'Завершение…'
            default:
                return null
        }
    })()

    return {
        handleRunPipeline,
        isPending: mutation.isPending,
        activeCompetitionUrl: mutation.isPending ? mutation.variables : undefined,
        step,
        stepLabel,
        error: mutation.error,
    }
}
